import { BEAT_STATES } from "./beatStates";

/**
 * terminalExam - the ONE definition of "this beat is the credential's final
 * exam", plus the small vocabulary the rail needs to render it.
 *
 * WHY A DERIVED PREDICATE AND NOT A SERVER FLAG. The backend already carries
 * the whole truth in two fields it now projects verbatim (`evidence_kind` and
 * `blocks_progression`), and contract 5.19 defines the certificate-only
 * exemption in exactly those terms: a mastery beat that does NOT block
 * progression is the terminal exam, because a 30-question floor quiz inside a
 * training block always blocks the block it sits in. Adding an
 * `is_terminal_exam` flag alongside them would create a second source of truth
 * that can disagree with the first.
 *
 * WHY `=== false` AND NOT FALSY. `blocks_progression` absent is the OLD
 * payload - a backend built before the projection landed. Strict false means an
 * old payload returns false here, the exam stays an ordinary training node, and
 * the surface renders exactly as it does in production today, including the
 * painted-done defect this work exists to fix. That is DOCUMENTED LEGACY
 * behaviour, not a fix, and the deploy-order gate on the PR is what makes it
 * unreachable: the backend ships and is verified live before this merges
 * (codex round-1 finding 5). A mixed-version test pins the legacy render so the
 * rollout dependency is visible rather than implied.
 */

/**
 * @param {object|null|undefined} beat - a beat from the clearance payload
 * @returns {boolean}
 */
export function isTerminalExamBeat(beat) {
  return (
    beat?.evidence_kind === "mastery_pass" && beat?.blocks_progression === false
  );
}

/**
 * findTerminalExamBeat - the capsule's terminal exam beat, or null.
 *
 * The adapter's invariant is at most ONE certificate-only mastery beat per
 * capsule. Should a payload ever carry two, the FIRST in block-then-sequence
 * order wins and the rail still renders a single terminal node, because two
 * "final" nodes is a worse answer than a deterministic one. Both are still
 * excluded from the training nodes (that exclusion is per-beat, in the rail
 * builder), so neither can reappear as `training N`.
 *
 * @param {object|null|undefined} clearance - {blocks: [{beats: []}]}
 * @returns {object|null}
 */
export function findTerminalExamBeat(clearance) {
  const blocks = Array.isArray(clearance?.blocks) ? clearance.blocks : [];
  for (const block of blocks) {
    const beats = Array.isArray(block?.beats) ? block.beats : [];
    for (const beat of beats) {
      if (isTerminalExamBeat(beat)) return beat;
    }
  }
  return null;
}

/**
 * terminalExamOpens - may a click on the terminal node open anything?
 *
 * ACTIVE only. A locked exam is not startable, a cleared one has nothing left
 * to do, and a failed_terminal one takes an instructor to reopen. The backend
 * refuses all three with a typed 409, and the rule on this surface is that we
 * never surface an action the backend will refuse (E2E F8).
 *
 * It is a named, exported rule rather than an inline comparison because it is
 * the SECOND of two gates - JourneyStep independently fires onClick only for
 * the node it renders active - and a second gate expressed inline is a gate no
 * probe can reach: removing it changes nothing observable, so a suite that
 * "covers" it covers it by coincidence. Named, it has its own test and its own
 * mutation probe, and the day someone changes the treatment rule this is still
 * standing.
 *
 * @param {object|null|undefined} beat
 * @returns {boolean}
 */
export function terminalExamOpens(beat) {
  return beat?.state === BEAT_STATES.ACTIVE;
}

/**
 * terminalNodeLabel - the rail node's one short label.
 *
 * The rail's rule everywhere else is that a node's label NAMES the step and its
 * state is visual treatment only. `failed_terminal` is the one place that rule
 * cannot hold on its own: JourneyStep has three treatments (done / current /
 * dim) and `locked` already owns dim, so a failed terminal exam would render
 * byte-identically to an exam the student has not reached yet. Those are
 * opposite facts about the world.
 *
 * So the label carries the distinction, in the lexicon beatPresentation already
 * uses for this exact state ("not cleared"). It is also what a screen reader
 * announces, since JourneyStep builds its aria-label from the description.
 *
 * @param {string|null|undefined} state - a BEAT_STATES value
 * @returns {string}
 */
export function terminalNodeLabel(state) {
  return state === BEAT_STATES.FAILED_TERMINAL ? "not cleared" : "final exam";
}
